import {
  Box,
  Avatar,
  Typography,
  Chip,
  Card,
  CardContent,
  Checkbox,
  Tooltip
} from "@mui/material";
import { Droppable, Draggable } from "react-beautiful-dnd";
import LaddaButton from "../../components/buttons/laddaButton";
import { icons } from "../../constants";
import {
  statusLabel,
  isDeleted,
  getTaskDisplayState,
  isTaskDisplayCompleted
} from "./taskHelpers";
import { StyledTooltip } from "../../components/toolltipComponent";

const coalesce = (...vals) =>
  vals.find((v) => v !== undefined && v !== null && v !== "") ?? "";

const toDate = (v) => (v ? new Date(String(v).replace(" ", "T")) : null);

const buildDateLabel = (task) => {
  if (task?.dateLabel) return task.dateLabel;
  const s = toDate(task?.initial_date);
  const e = toDate(task?.end_date);
  if (!s && !e) return "";
  const opts = (d) =>
    d?.toLocaleDateString("es-ES", { day: "numeric", month: "long" }) ?? "";
  if (s && e) {
    const sameDay =
      s.getFullYear() === e.getFullYear() &&
      s.getMonth() === e.getMonth() &&
      s.getDate() === e.getDate();
    return sameDay ? opts(s) : `${opts(s)} al ${opts(e)}`;
  }
  return s ? opts(s) : opts(e);
};

const statusColor = (task) => {
  if (isDeleted(task)) return "error";
  if (isTaskDisplayCompleted(task)) return "success";
  switch (getTaskDisplayState(task)) {
    case 1:
      return "default";
    case 2:
      return "warning";
    case 3:
      return "success";
    default:
      return "default";
  }
};

const getBadges = (task) => {
  const badges = [];

  if (isDeleted(task)) {
    badges.push({ label: "Eliminada", color: "error" });
  } else if (isTaskDisplayCompleted(task)) {
    badges.push({ label: "Completada", color: "success" });
  } else if (getTaskDisplayState(task)) {
    badges.push({ label: statusLabel(task), color: statusColor(task) });
  }

  return badges;
};

const CAL_COLORS = {
  A: "#d30a06ff",
  B: "#ff9800",
  C: "#485ff3ff",
  D: "#2e7d32",
  DEFAULT: "#4444B2"
};

const getInitials = (name) =>
  (name || "")
    .trim()
    .split(/\s+/)
    .map((n) => n[0] || "")
    .join("")
    .slice(0, 2)
    .toUpperCase();

const toHHMM = (s) => (s ? String(s).slice(0, 5) : null);

const buildTimeBadge = (t) => {
  const start = toHHMM(t?.start_hour) || toHHMM(t?.start_hour);
  const end = toHHMM(t?.end_hour) || toHHMM(t?.end_hour);

  return start && end ? `${start} – ${end}` : null;
};

const TaskCard = ({
  task,
  onToggleComplete,
  onClick,
  completed,
  positionLabel,
  onRequestComplete,
  showCheck,
  noEditable,
  showDelete,
  onOpenDetails,
  onOpenDelete
}) => {
  const mainText = coalesce(task?.description, "(Sin descripción)");
  const subProject = coalesce(task?.process?.name, "");
  const datePart = buildDateLabel(task);

  const cal = (task?.calification || "").toUpperCase();
  const calColor = CAL_COLORS[cal] || CAL_COLORS.DEFAULT;
  const calText = ["A", "B", "C", "D"].includes(cal) ? cal : "?";

  const calDisplay = task?.cal_position_label || calText;
  const assigneeName = task?.employer?.name || "";
  const assigneeInitials = getInitials(assigneeName);

  const bgColor = isDeleted(task)
    ? "#fdecea" // rojizo suave
    : isTaskDisplayCompleted(task)
    ? "#e6f9e6"
    : task?.reschedule === 1
    ? "#fff8e1"
    : task?.reschedule === 2
    ? "#ffe0b2"
    : task?.reschedule === 3
    ? "#ffe0b2"
    : "white";

  const showCalBall = !isTaskDisplayCompleted(task) && !!calDisplay;
  const timePart = buildTimeBadge(task);

  return (
    <Card
      onClick={onClick}
      sx={{
        width: "100%",
        mb: 1.5,
        border: "1px solid #ddd",
        bgcolor: bgColor,
        cursor: noEditable ? "default" : "pointer",
        transition: "background-color 0.2s ease",
        borderRadius: 4.5,
        boxShadow: "none",
        display: "block"
      }}
    >
      <CardContent
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          p: 1.5,
          px: 3,
          flexWrap: "wrap",
          gap: 1
        }}
      >
        <Box display="flex" alignItems="center" minWidth={0} flex={1}>
          {showCheck && (
            <Checkbox
              size="small"
              checked={isTaskDisplayCompleted(task)}
              sx={{ mr: 1 }}
              onChange={(e) => {
                e.stopPropagation();
                if (typeof onRequestComplete === "function") {
                  onRequestComplete(task);
                } else if (typeof onToggleComplete === "function") {
                  onToggleComplete(task.id);
                }
              }}
              onClick={(e) => e.stopPropagation()}
            />
          )}

          <Box sx={{ overflow: "hidden", minWidth: 0 }}>
            <Typography
              variant="subtitle2"
              sx={{
                textDecoration: isTaskDisplayCompleted(task) ? "line-through" : "none",
                color: isDeleted(task)
                  ? "#b71c1c" // texto más rojito para eliminadas
                  : isTaskDisplayCompleted(task)
                  ? "gray"
                  : "inherit",
                wordBreak: "break-word"
              }}
            >
              <b>{mainText}</b>
            </Typography>

            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: "block", whiteSpace: "normal" }}
            >
              <b>{subProject}</b>

              <p className="m-0">
                {datePart ? ` • ${datePart}` : ""}
                {timePart ? ` • ${timePart}` : ""}
              </p>
            </Typography>
          </Box>
        </Box>

        <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
          {getBadges(task).map((b, i) => (
            <Chip
              key={i}
              label={b.label}
              size="small"
              variant="outlined"
              color={
                b.color === "success"
                  ? "success"
                  : b.color === "error"
                  ? "error"
                  : "default"
              }
              sx={{
                fontSize: "0.75rem",
                bgcolor: isDeleted(task)
                  ? "#fcd2cf"
                  : isTaskDisplayCompleted(task)
                  ? "#bff0b3"
                  : "white",
                border: isDeleted(task)
                  ? "0px"
                  : isTaskDisplayCompleted(task)
                  ? "0px"
                  : task?.reschedule
                  ? "0px"
                  : "1px solid #ddd",
                p: "3px"
              }}
            />
          ))}

          <Tooltip
            title={assigneeName ? `Asignado a ${assigneeName}` : "Sin asignar"}
            arrow
          >
            <Avatar
              sx={{
                bgcolor: "#ECEEF7",
                color: "#3F3FB2",
                width: 32,
                height: 32,
                fontSize: "0.80rem",
                fontWeight: 700
              }}
            >
              {assigneeInitials || "?"}
            </Avatar>
          </Tooltip>

          {showCalBall && (
            <Tooltip title={`Clasificación ${calDisplay}`} arrow>
              <Avatar
                sx={{
                  bgcolor: calColor,
                  width: 32,
                  height: 32,
                  fontSize: "0.80rem",
                  fontWeight: 700
                }}
              >
                {calDisplay}
              </Avatar>
            </Tooltip>
          )}

          <Avatar
            onClick={(e) => {
              e.stopPropagation();
              if (typeof onOpenDetails === "function") onOpenDetails(task);
            }}
            sx={{
              bgcolor: "#6A6AD8",
              width: 32,
              height: 32,
              fontSize: "0.80rem",
              fontWeight: 700,
              cursor: "pointer"
            }}
          >
            <img className="btn-icon" src={icons.historialWhite} alt="icon" />
          </Avatar>

          {showDelete && (
            <Avatar
              onClick={(e) => {
                e.stopPropagation();
                if (typeof onOpenDelete === "function") onOpenDelete(task);
              }}
              sx={{
                bgcolor: "#c95d42ff",
                width: 32,
                height: 32,
                fontSize: "0.80rem",
                fontWeight: 700,
                cursor: "pointer"
              }}
            >
              <img className="btn-icon" src={icons.trash} alt="icon" />
            </Avatar>
          )}
        </Box>
      </CardContent>
    </Card>
  );
};

const TaskSection = ({
  title,
  dateRange,
  tasks,
  isDraggable,
  setOpenModal,
  setSelectedTask,
  showAddButton,
  onRequestComplete,
  droppableId,
  completed = false,
  showCheck = false,
  noEditable = false,
  canEditTask,
  canDeleteTask,
  onOpenDetails,
  onOpenDelete,
  titleTooltip
}) => {
  const safeTasks = Array.isArray(tasks) ? tasks : [];

  return (
    <Box
      sx={{
        width: "100%",
        bgcolor: "white",
        border: "1px solid #ddd",
        borderRadius: 2,
        p: 3,
        mb: 3,
        minWidth: 0
      }}
    >
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
        flexWrap="wrap"
        gap={2}
      >
        <div className="HContainer align-center">
          <div className="VContainer mr-20">
            <Typography variant="h6" color="#4444B2" fontWeight="bold">
              {title}
            </Typography>
            {dateRange ? (
              <Typography variant="body2" color="text.secondary">
                {dateRange}
              </Typography>
            ) : null}
          </div>

          {titleTooltip ? (
            <StyledTooltip title={titleTooltip()} placement="top">
              <img
                className="btn-icon-20 pointer "
                src={icons.info_blue}
                alt="icon"
              />
            </StyledTooltip>
          ) : (
            <></>
          )}
        </div>

        {showAddButton && (
          <LaddaButton
            variant="contained"
            height={"32px"}
            onClick={() => {
              setSelectedTask(null);
              setOpenModal(true);
            }}
          >
            <div className="IHContainer align-center">
              <img
                className="btn-icon"
                src={icons.plusIcon}
                alt="icon"
                style={{ marginRight: "10px" }}
              />
              <p className="button_text">Agregar Tarea</p>
            </div>
          </LaddaButton>
        )}
      </Box>

      <Droppable droppableId={droppableId} isDropDisabled={!isDraggable}>
        {(provided) => (
          <div {...provided.droppableProps} ref={provided.innerRef}>
            {safeTasks.length === 0 && (
              <Typography variant="body2" color="text.secondary">
                Sin tareas para mostrar.
              </Typography>
            )}

            {safeTasks.map((task, index) => {
              const key = String(task.id);
              const taskIsEditable =
                typeof canEditTask === "function" ? canEditTask(task) : !noEditable;
              const taskCanDelete =
                typeof canDeleteTask === "function" ? canDeleteTask(task) : !noEditable;
              const card = (
                <TaskCard
                  task={task}
                  onRequestComplete={onRequestComplete}
                  onClick={() => {
                    if (!taskIsEditable) return;
                    setSelectedTask(task);
                    setOpenModal(true);
                  }}
                  completed={completed}
                  positionLabel={index + 1}
                  showCheck={showCheck}
                  noEditable={!taskIsEditable}
                  showDelete={taskCanDelete}
                  onOpenDetails={onOpenDetails}
                  onOpenDelete={
                    taskCanDelete
                      ? (t) => {
                          onOpenDelete?.(t || task);
                        }
                      : undefined
                  }
                />
              );

              return isDraggable ? (
                <Draggable key={key} draggableId={key} index={index}>
                  {(providedDr) => (
                    <div
                      ref={providedDr.innerRef}
                      {...providedDr.draggableProps}
                      {...providedDr.dragHandleProps}
                    >
                      {card}
                    </div>
                  )}
                </Draggable>
              ) : (
                <div key={key}>{card}</div>
              );
            })}

            {provided.placeholder}
          </div>
        )}
      </Droppable>
    </Box>
  );
};

export default TaskSection;
